import React, { useEffect, useState, useRef } from 'react';
import { getPaddleInstance, isPaddleReady, getPaddleStatus } from '../services/paddlePreloader';
import { PADDLE_PRODUCT_ID, PRODUCT_PRICE } from '../config/paddle';
import { useTranslation } from 'react-i18next';
import { log } from '../utils/logger';

// Placeholder SVG for checkmark
const CheckIcon = ({ className = "h-5 w-5 text-orange-500" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  const paddleRef = useRef<any>(null);
  const [paddleReady, setPaddleReady] = useState(isPaddleReady());
  const [paddleStatus, setPaddleStatus] = useState(getPaddleStatus());

  const getPaddleLocale = () => {
    if (i18n.language.startsWith('zh')) return 'zh-Hans';
    return 'en';
  };

  // 初始化获取预加载的 Paddle 实例
  useEffect(() => {
    const initPaddle = async () => {
      try {
        setPaddleStatus('loading');
        paddleRef.current = await getPaddleInstance();
        setPaddleReady(true);
        setPaddleStatus('ready');
      } catch (error) {
        setPaddleReady(false);
        setPaddleStatus('error');
        log.error('获取 Paddle 实例失败', error);
      }
    };

    if (isPaddleReady()) {
      getPaddleInstance().then(instance => {
        paddleRef.current = instance;
        setPaddleReady(true);
        setPaddleStatus('ready');
      });
    } else {
      initPaddle();
    }
  }, []);

  // 监听 Paddle 支付事件
  useEffect(() => {
    const handlePaddleMessage = (event: MessageEvent) => {
      if (event.data && event.data.action === 'event') {
        if (event.data.event_name === 'checkout.completed') {
          log.info('🎉 支付成功！');
          setPurchaseSuccess(true);
        }
      }
    };

    window.addEventListener('message', handlePaddleMessage);
    return () => window.removeEventListener('message', handlePaddleMessage);
  }, []);

  // 点击唤起 Paddle 结账弹窗
  const handleBuy = async () => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      const paddle = paddleRef.current || await getPaddleInstance();


      if (!paddleReady || !paddleRef.current) {
        throw new Error(t('pricing.errorNotReady', 'Payment module is not ready yet. Please try again.'));
      }

      const checkoutOptions: any = {
        settings: {
          displayMode: 'overlay',
          theme: 'dark',
          locale: getPaddleLocale(),
          successUrl: `${window.location.origin}/pricing?payment_status=success`,
        },
        items: [
          {
            priceId: PADDLE_PRODUCT_ID,
            quantity: 1,
          }
        ],
      };

      await paddleRef.current.Checkout.open(checkoutOptions);
      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      log.error('唤起 Paddle 支付失败', error);
      alert(error instanceof Error ? error.message : t('pricing.errorOpenFailed', 'Failed to open checkout window, please refresh and retry.'));
    }
  };

  const proFeatures = [
    t('pricing.features.packer', '突破 200 张上限：无限图元纳管、多页图集自动分包与 4K/8K 超高清导出'),
    t('pricing.features.slicer', '4合1 智能切片（Alpha识别 / 规则网格 / 绿幕抠图 / 自由分割）与批量重命名'),
    t('pricing.features.animation', '动效质检播放与导出透明 GIF / APNG / WebP 动图（带 1x~8x 像素级放大）'),
    t('pricing.features.engines', '边缘像素挤出 (Extrude) 防黑边渗色、10 大主流引擎与自定义模板导出'),
    t('pricing.features.offline', '100% 纯本地离线运行，零云端上传，商业美术资产绝对保密'),
    t('pricing.features.updates', '终身买断授权，单 Key 支持激活 3 台个人设备（支持自助解绑）与免费更新'),
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-900 py-12 md:py-20 min-h-screen">
      <div className="mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block py-1 px-3 mb-3 text-xs font-semibold uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-950/60 dark:text-orange-400 rounded-full">
            {t('pricing.badge', 'Lifetime License · No Ads')}
          </span>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
            {t('pricing.title', 'Simple, Transparent Pricing')}
          </h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
            {t('pricing.subtitle', 'Pay once, use forever. No hidden subscriptions.')}
          </p>
        </div>

        {/* 支付成功提示 */}
        {purchaseSuccess && (
          <div className="mb-8 p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-700 rounded-xl text-emerald-800 dark:text-emerald-200 text-center">
            <h3 className="text-xl font-bold">{t('pricing.successTitle', '🎉 Congratulations! Purchase Successful!')}</h3>
            <p className="mt-2 text-sm">{t('pricing.successMessage', 'Your lifetime License Key has been sent to your email. Thank you for supporting AtlasSlicer!')}</p>
          </div>
        )}

        {/* Pricing Card */}
        <div className="max-w-lg mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl border-2 border-orange-500 overflow-hidden">
          <div className="p-8 sm:p-10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('pricing.planTitle', 'AtlasSlicer Pro License')}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('pricing.planSubtitle', 'For indie developers, game studios, and 2D artists')}
                </p>
              </div>
              <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                PRO
              </span>
            </div>

            <div className="mt-6 flex items-baseline">
              <span className="text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                ${PRODUCT_PRICE.amount}
              </span>
              <span className="ml-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                / {t('pricing.lifetime', 'Lifetime')}
              </span>
            </div>

            {/* Paddle 购买按钮 */}
            <button
              type="button"
              onClick={handleBuy}
              disabled={!paddleReady || isProcessing}
              className="mt-8 block w-full rounded-xl bg-orange-500 px-6 py-4 text-center text-lg font-bold text-white shadow-lg hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform active:scale-95"
            >
              <div className="flex items-center justify-center">
                {isProcessing && (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>
                  {isProcessing
                    ? t('common.processing', 'Processing...')
                    : t('pricing.buyNowBtn', 'Buy Securely via Paddle')
                  }
                </span>
              </div>
            </button>

            {/* 支付系统状态 */}
            {paddleStatus !== 'ready' && (
              <p className="mt-3 text-center text-xs text-gray-400">
                {paddleStatus === 'loading'
                  ? t('pricing.connectingGateway', 'Connecting to secure payment gateway...')
                  : t('pricing.preparingPayment', 'Preparing payment component...')
                }
              </p>
            )}

            <div className="mt-8 border-t border-gray-100 dark:border-gray-700 pt-8">
              <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-200 tracking-wider uppercase">
                {t('pricing.includes', 'Everything included:')}
              </h4>
              <ul className="mt-4 space-y-3 text-sm text-gray-600 dark:text-gray-300">
                {proFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckIcon className="h-5 w-5 flex-shrink-0 text-orange-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="px-8 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center justify-center gap-2">
            <span>🔒 {t('pricing.securityBadge', 'Paddle Official Secure Checkout')}</span>
            <span>·</span>
            <span>{t('pricing.paymentMethods', 'Supports Visa, MasterCard, PayPal')}</span>
            <span>·</span>
            <span>{t('pricing.moneyBackGuarantee', '14-Day Money-Back Guarantee')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage; 